import type { PuzzleEnvelope } from '../games/core/types';
import type { NonogramPuzzle } from '../games/nonogram/types';
import type { GeneratorResponse } from './messages';

export type GenerationProgress = {
  completed: number;
  total: number;
  gameName: string;
};

export type GenerationJob = {
  promise: Promise<PuzzleEnvelope[]>;
  cancel: () => void;
};

export function generateRunInWorker(
  seed: string,
  onProgress: (progress: GenerationProgress) => void,
  recentNonograms: NonogramPuzzle[] = [],
): GenerationJob {
  const worker = new Worker(new URL('./generator.worker.ts', import.meta.url), { type: 'module' });
  const requestId = crypto.randomUUID();
  let settled = false;
  let rejectJob: ((reason?: unknown) => void) | null = null;

  const promise = new Promise<PuzzleEnvelope[]>((resolve, reject) => {
    rejectJob = reject;
    worker.onmessage = (event: MessageEvent<GeneratorResponse>) => {
      if (settled) return;
      const response = event.data;
      if (response.requestId !== requestId) return;
      if (response.type === 'progress') onProgress(response);
      if (response.type === 'complete') {
        settled = true;
        resolve(response.puzzles);
        worker.terminate();
      }
      if (response.type === 'cancelled') {
        settled = true;
        reject(new DOMException('Generation cancelled', 'AbortError'));
        worker.terminate();
      }
      if (response.type === 'error') {
        settled = true;
        reject(new Error(response.message));
        worker.terminate();
      }
    };
    worker.onerror = (event) => {
      if (settled) return;
      settled = true;
      reject(new Error(event.message || 'Puzzle worker failed'));
      worker.terminate();
    };
    worker.postMessage({ type: 'generate-run', requestId, seed, recentNonograms });
  });

  return {
    promise,
    cancel: () => {
      if (settled) return;
      settled = true;
      worker.postMessage({ type: 'cancel', requestId });
      worker.terminate();
      rejectJob?.(new DOMException('Generation cancelled', 'AbortError'));
    },
  };
}
